import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useAccountData from 'src/hooks/useAccountData';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
	User,
} from '@velocity-exchange/sdk';
import { getMarketFees } from 'src/utils/market';
import {
	MarketId,
	OpenPosition,
	UIMarket,
	UIOrderType,
	roundToStepSizeIfLargeEnough,
	getBaseAssetSymbol,
} from '@velocity-exchange/common';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import { useState } from 'react';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import useClosePositionStateEngine from 'src/hooks/positions/useClosePositionStateEngine';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useDriftClient from 'src/hooks/useDriftClient';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useOraclePrice from 'src/hooks/useOraclePrice';
import useExecuteClosePosition from 'src/hooks/positions/useExecuteClosePosition';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';

interface Params {
	marketIndex: number;
	uiOrderType: UIOrderType;
	onClosePositionSuccess: () => void;
}

/**
 * Hook shared by `ClosePositionModal` and `TpSlOrderFormPopup`.
 * Sets up / returns the bulk of what's needed to close a position.
 */
export default function useClosePositionUtils({
	marketIndex,
	uiOrderType,
	onClosePositionSuccess,
}: Params) {
	const marketId = new MarketId(marketIndex, MarketType.PERP);
	const uiMarket = UIMarket.createPerpMarket(marketIndex);

	const driftClient = useDriftClient();
	const userAccount = useAccountData();
	const showAccountValues = useShowAccountValues();
	const currentMarkPrice = useMarkPrice(marketId);
	const stepSize = useMarketStepSize(marketId);
	const getTickSizeForMarket = useGetTickSizeForMarket();
	const [currentSettings, setCurrentSettings] = useCurrentSettings();
	const targetMarketInfo = useMarketsInfoStore((s) =>
		s.getMarketInfoByIndexAndType(marketIndex, MarketType.PERP)
	);
	const tradeFormSlippageTolerance = useDriftStore(
		(s) => s.tradeForm.slippageTolerance
	);
	const oraclePrice = useOraclePrice(marketId);
	const openPerpPositions = useCurrentAccountPositions();
	const position = openPerpPositions.find(
		(position) => position.marketIndex === marketIndex
	);

	const openPositionSizeBigNum = BigNum.from(
		position?.baseSize.abs(),
		BASE_PRECISION_EXP
	);

	// Determine the close order side (opposite of position direction)
	const closeOrderSide: 'buy' | 'sell' =
		position?.direction === 'long' ? 'sell' : 'buy';

	// Use the position size for close order preview
	const positionSizeString = openPositionSizeBigNum.abs().toNum().toString();

	const previewOrderParams = usePreviewOrder(marketId, {
		baseSizeStringValue: positionSizeString,
		side: closeOrderSide,
		orderType: uiOrderType,
		reduceOnly: true, // Close orders are always reduce-only
	});

	const userAccountClient = userAccount?.client;

	const markPriceToUse = currentMarkPrice;

	const maxBaseSizeString = roundToStepSizeIfLargeEnough(
		openPositionSizeBigNum.abs().toNum().toString(),
		stepSize
	);

	const tickSize = getTickSizeForMarket(marketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const [priceInputValue, setPriceInputValue] = useState(() =>
		markPriceToUse.toFixed(tickSizeDecimalPlaces)
	);
	const [secondaryPriceInputValue, setSecondaryPriceInputValue] = useState(() =>
		markPriceToUse.toFixed(tickSizeDecimalPlaces)
	);
	const [baseSizeInputValue, setBaseSizeInputValue] =
		useState(maxBaseSizeString);

	const baseAssetSymbol = getBaseAssetSymbol(position?.marketSymbol ?? '');

	const baseSizeInputValueBigNum = BigNum.fromPrint(
		baseSizeInputValue,
		BASE_PRECISION_EXP
	);

	const priceInputValueBigNum = BigNum.fromPrint(
		priceInputValue,
		PRICE_PRECISION_EXP
	);

	const secondaryPriceInputValueBigNum = BigNum.fromPrint(
		secondaryPriceInputValue ?? '0',
		PRICE_PRECISION_EXP
	);

	const { takerFeeBps } = getMarketFees(
		userAccountClient,
		MarketType.PERP,
		marketIndex,
		driftClient
	);

	const {
		errorMessageInfo,
		realisedPnl,
		pnlBeforeFees,
		notionalSizeAtEntry,
		notionalSizeAtExit,
		takerFee,
		liquidationPrice: newLiquidationPrice,
		showSecondaryPriceBox,
		priceBoxHeading,
		quoteSizeOfClose: autoCalculatedQuoteSize,
		priceImpactInfo,
	} = useClosePositionStateEngine({
		market: marketIndex,
		selectedOrderType: uiOrderType,
		priceBoxValue: Number(priceInputValue),
		secondaryPriceBoxValue: Number(secondaryPriceInputValue),
		currentPositionDirection: position?.direction ?? 'long',
		openPositionInfo: position as OpenPosition,
		targetMarketTickSizeExponent:
			targetMarketInfo?.genericInfo?.tickSizeExponent ?? 2,
		takerFeeBps,
		baseAmountOfClose: baseSizeInputValueBigNum,
		user: userAccountClient as User,
	});

	const slippage = previewOrderParams.slippage;

	const { closePosition, isPending: isClosingPosition } =
		useExecuteClosePosition({
			marketIndex,
			uiOrderType,
			baseSize: baseSizeInputValueBigNum,
			onSuccess: onClosePositionSuccess,
			limitPrice:
				uiOrderType !== 'market'
					? uiOrderType === 'takeProfitLimit' || uiOrderType === 'stopLimit'
						? secondaryPriceInputValueBigNum
						: priceInputValueBigNum
					: undefined,
			triggerPrice:
				uiOrderType === 'takeProfitLimit' || uiOrderType === 'stopLimit'
					? priceInputValueBigNum
					: undefined,
			shouldCaptureEvent: true,
		});

	return {
		closePosition,
		isClosingPosition,
		slippage,
		showAccountValues,
		currentSettings,
		setCurrentSettings,
		priceInputValue,
		setPriceInputValue,
		baseSizeInputValue,
		setBaseSizeInputValue,
		baseSizeInputValueBigNum,
		openPositionSizeBigNum,
		maxBaseSizeString,
		baseAssetSymbol,
		markPriceToUse,
		position,
		stepSize,
		autoCalculatedQuoteSize,
		errorMessageInfo,
		priceImpactInfo,
		// The following are only used for `TpSlOrderFormPopup`.
		closeOrderSide,
		uiMarket,
		marketId,
		realisedPnl,
		takerFee,
		pnlBeforeFees,
		notionalSizeAtEntry,
		notionalSizeAtExit,
		secondaryPriceInputValue,
		setSecondaryPriceInputValue,
		newLiquidationPrice,
		priceBoxHeading,
		showSecondaryPriceBox,
		driftClient,
		tradeFormSlippageTolerance,
		targetMarketInfo,
		userAccount,
		oraclePrice,
		currentMarkPrice,
	};
}
